import { type PropsWithChildren } from "react"
import { SubscriptionLayout } from "@/features/subscription"
import { StickyReinsurances } from "@/features/subscription/reinsurances/components/StickyReinsurances"
import { type OnboardingRouteNames } from "@/routes/onboarding"
import { storyblokRoutesPaths } from "@/routes/storyblok"
import {
    ONBOARDING_STEPS_WITH_HOME_REDIRECTION,
    ONBOARDING_STEPS_WITHOUT_BANNER,
    useOnboardingStepper,
} from "../stepper"
import {
    ONBOARDING_STEP_WITH_STICKY_REINSURANCES,
    ONBOARDING_STEP_WITH_TRUSTPILOT,
    type OnboardingSerializedState,
    onboardingWizard,
} from "../wizard"
import { OnboardingBanner } from "./OnboardingBanner"
import { ResetSessionButton } from "./ResetSessionButton"

type OnboardingLayoutProps = {
    serializedState: OnboardingSerializedState
    stepName: OnboardingRouteNames
}

export function OnboardingLayout({
    serializedState,
    stepName,
    children,
}: PropsWithChildren<OnboardingLayoutProps>) {
    const onboardingState = onboardingWizard.deserializeState(serializedState)

    const homeRedirection = ONBOARDING_STEPS_WITH_HOME_REDIRECTION.includes(stepName)
        ? storyblokRoutesPaths.home.resolve()
        : undefined

    const journeySteps = onboardingWizard.getSteps(onboardingState)

    const { hasStepper, stepperProps } = useOnboardingStepper({
        journeySteps,
        stepName,
        onboardingState,
    })

    const isHeadlessLayout = !!onboardingState.formValues.headless

    const hasPartnerSlug = !!onboardingState.formValues.partnerSlug
    const isNavigator = onboardingState.formValues.subscriptionType === "NAVIGATOR"
    const isPatnerOrNavigator = hasPartnerSlug || isNavigator

    // Sellers need to be able to purge the session on any step so that data from
    // a previous customer never leaks into the next quote.
    const withResetSession = isNavigator && !isHeadlessLayout

    const withStepper =
        hasStepper &&
        !isHeadlessLayout &&
        onboardingState.formValues.subscriptionType !== "CHECKOUT_PAGES"

    const withStickyReinsurance =
        !isHeadlessLayout &&
        !isPatnerOrNavigator &&
        ONBOARDING_STEP_WITH_STICKY_REINSURANCES.includes(stepName)

    const withTrustpilot =
        !isHeadlessLayout &&
        !isPatnerOrNavigator &&
        ONBOARDING_STEP_WITH_TRUSTPILOT.includes(stepName)

    const isRecommendedSubscribedMaxPowerSelected =
        !!onboardingState.formValues.userInput.electricitySubscribedMaxPower &&
        !!onboardingState.formValues.electricity.supplyPoint.meterPoint.subscribedMaxPower &&
        onboardingState.formValues.userInput.electricitySubscribedMaxPower !==
            onboardingState.formValues.electricity.supplyPoint.meterPoint.subscribedMaxPower

    return (
        <>
            {!hasPartnerSlug && !ONBOARDING_STEPS_WITHOUT_BANNER.includes(stepName) && (
                <OnboardingBanner
                    affiliateCode={onboardingState.formValues.affiliateCode}
                    journeyType={onboardingState.formValues.journeyType}
                    reward={onboardingState.formValues.reward}
                    recommendedSubscribedMaxPower={
                        isRecommendedSubscribedMaxPowerSelected
                            ? onboardingState.formValues.userInput.electricitySubscribedMaxPower
                            : null
                    }
                />
            )}
            <SubscriptionLayout
                stepName={stepName}
                stepperProps={stepperProps}
                withStepper={withStepper}
                partner={onboardingState.formValues.partnerSlug}
                withTrustpilot={withTrustpilot}
                withPrivacyPolicy={!hasPartnerSlug}
                withStickyReinsurance={withStickyReinsurance}
                isHeadless={onboardingState.formValues.headless}
                href={homeRedirection}
            >
                {children}
                {withResetSession && (
                    <ResetSessionButton
                        partnerSlug={onboardingState.formValues.partnerSlug}
                        affiliateCode={onboardingState.formValues.affiliateCode}
                    />
                )}
            </SubscriptionLayout>
        </>
    )
}

OnboardingLayout.messages = [
    ...SubscriptionLayout.messages,
    ...StickyReinsurances.messages,
    ...OnboardingBanner.messages,
    ...ResetSessionButton.messages,
    ...useOnboardingStepper.messages,
] as const
